import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { ENTITY_LOG_KEY } from '@/shared/hooks/use-workflows'
import { needsExceptionCapture } from './exception-capture'

import {
  CardActionMenu,
  type CardActionMenuItem,
} from '@/admin/components/shared/card-action-menu'
import { EditFormDialog } from '@/admin/components/shared/edit-form-dialog'
import { EntityActionExecuteDialog } from '@/admin/components/shared/entity-action-execute-dialog'
import { ExceptionModal } from '@/admin/components/workflow-issues/workflow-issue-resolve-modal'
import {
  useEvaluatedEntityActions,
  useExecuteEntityAction,
  type EntityAction,
} from '@/admin/hooks/use-entity-actions'
import { useEntityTypeByKey } from '@/admin/hooks/use-entities'
import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import { useCertificateDownloadEnabled } from '@/admin/hooks/use-registration-settings'
import { LucideIconByName } from '@/shared/components/ui/lucide-icon-by-name'
import {
  useCreateExceptionGrant,
  useExceptionActionBindings,
  useExceptionReasons,
} from '@/shared/hooks/use-workflows'
import type {
  ExceptionActionBinding,
  WorkflowIssueReasonOption,
  WorkflowIssueResolvePayload,
} from '@/shared/lib/workflows/types'
import { AddressLookupProvider } from '@/shared/components/ui/address-lookup-context'
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context'
import { defineField, type FormFieldMeta } from '@/shared/lib/forms'
import api from '@/admin/lib/api'
import { createAddressLookupApi } from '@/admin/hooks/use-lookups'
import { useResourceLookupProviders } from '@/admin/hooks/use-resource-lookup-providers'
import { toast } from 'sonner'
import { Lock } from 'lucide-react'
import { TOAST_DURATION } from '@/shared/lib/toast-duration'
import { TierChange, TierSwatch } from '@/shared/components/ui/tier-badge'
import { toastApiError } from '@/shared/lib/api-error'
import { AddressMatchNotice } from '@/admin/components/shared/address-matches'

interface EntityActionMenuBaseProps {
  resourceUuid: string
  /** The registration this menu sits on, when the resource belongs to one. */
  holdingUuid?: string
  onNavigate?: (action: EntityAction) => void
  onEditFields?: (action: EntityAction) => void
  onActionComplete?: () => void
  triggerClassName?: string
  triggerLabel?: string
  triggerTestId?: string
  contentClassName?: string
  externalTriggerActionKey?: string | null
  onExternalTriggerReset?: () => void
  /**
   * Action keys to hide from this menu instance. Use when the surface already
   * exposes an action through dedicated UI (e.g. the warranty card renders a
   * "Certificate" button, so it hides the ``print_certificate`` menu item to
   * avoid a duplicate). The action still exists in the catalog for other
   * surfaces - this is presentation-only, not a capability change.
   */
  excludeActionKeys?: string[]
  /** Show only actions run by these handlers (e.g. a card that owns one capability). */
  onlyHandlerKeys?: string[]
  /** Hide actions run by these handlers (they live on another card). */
  excludeHandlerKeys?: string[]
  /**
   * Render the action dialogs without the "..." trigger. Use when the surface
   * drives every action from its own controls via
   * ``externalTriggerActionKey`` and would otherwise show an empty menu.
   */
  hideTrigger?: boolean
  /**
   * Items appended below the configured entity actions.
   *
   * For surface-owned actions that are not workflow-configured and carry no
   * server-side capability of their own - a download that just re-renders
   * data the caller can already see, for instance. Configured behaviour
   * belongs in an ``EntityAction`` row, not here.
   */
  extraItems?: CardActionMenuItem[]
}

type EntityActionMenuProps = EntityActionMenuBaseProps &
  (
    | { entityTypeUuid: string; entityTypeKey?: never }
    | { entityTypeKey: string; entityTypeUuid?: never }
  )

/**
 * Humanise a snake_case column name into a readable label.
 * e.g. "address_line_1" → "Address line 1", "address_postcode" → "Address postcode"
 */
function humaniseColumnName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
}

/**
 * For composite fields (ADDRESS, ENTITY_SEARCH), the backend stores
 * components in separate columns while the form uses a composite object.
 * The mapping lives in configuration_data.component_columns on the
 * attribute, e.g. { "addressLine1": "address_line_1" } for ADDRESS
 * or { "uuid": "partnership", "name": "partnership__name" } for
 * ENTITY_SEARCH.
 */
function composeCompositeValue(
  componentColumns: Record<string, string>,
  resourceValues: Record<string, unknown>,
): Record<string, string> {
  const result: Record<string, string> = {}
  for (const [compositeKey, columnName] of Object.entries(componentColumns)) {
    result[compositeKey] = (resourceValues[columnName] as string) ?? ''
  }
  return result
}

function decomposeCompositeValue(
  componentColumns: Record<string, string>,
  compositeValue: Record<string, string>,
): Record<string, string> {
  const result: Record<string, string> = {}
  for (const [compositeKey, columnName] of Object.entries(componentColumns)) {
    // Skip read-only display columns (e.g. "name" mapped to
    // "partnership__name") - these use dunder notation to indicate
    // they are derived from a related object and are not writable.
    if (columnName.includes('__')) continue
    result[columnName] = compositeValue[compositeKey] ?? ''
  }
  return result
}


/**
 * The finish swatch shown beside a tier action.
 *
 * Reads the destination's finish token out of the action's own handler config,
 * which the seed resolves from the tier row - so the menu cannot disagree with
 * the badge on the record.
 */
/**
 * The menu section an action is drawn under, from its own data
 * (``handler_config.menu_section``). Undefined for an action with none.
 */
function menuSection(action: EntityAction): string | undefined {
  const value = action.handler_config?.menu_section
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function tierSwatchFor(
  action: EntityAction,
  enabled: boolean,
): React.ReactNode | null {
  if (!enabled) return null
  const config = (action.handler_config ?? {}) as Record<string, unknown>
  const finish = config.target_finish
  if (typeof finish !== 'string' || !finish || finish === 'neutral') return null
  return <TierSwatch finish={finish} />
}


/**
 * Actions that collect input first: field edits, and commands whose config opts
 * in with ``confirmation.collect_fields`` (opt-in, so existing commands that
 * list capture fields keep their plain confirm).
 */
function collectsFields(action: EntityAction): boolean {
  return (
    action.action_kind === 'edit_fields' ||
    (action.action_kind === 'command' &&
      Boolean(action.confirmation?.collect_fields) &&
      (action.capture_fields?.length ?? 0) > 0)
  )
}

export function EntityActionMenu({
  entityTypeUuid,
  entityTypeKey,
  resourceUuid,
  holdingUuid,
  onNavigate,
  onEditFields,
  onActionComplete,
  triggerClassName,
  triggerLabel,
  triggerTestId,
  contentClassName,
  externalTriggerActionKey,
  onExternalTriggerReset,
  excludeActionKeys,
  onlyHandlerKeys,
  excludeHandlerKeys,
  hideTrigger,
  extraItems,
}: EntityActionMenuProps) {
  const { data: resolvedEntity } = useEntityTypeByKey(
    entityTypeKey && !entityTypeUuid ? entityTypeKey : undefined,
  )
  const resolvedUuid = entityTypeUuid ?? resolvedEntity?.uuid
  const resolvedKey = entityTypeKey ?? resolvedEntity?.key

  const { data: evalResult } = useEvaluatedEntityActions(
    resolvedUuid,
    resourceUuid,
    holdingUuid,
  )
  const actions = evalResult?.actions
  const resourceValues = useMemo(
    () => evalResult?.resource_values ?? {},
    [evalResult?.resource_values],
  )
  // Attribute definitions for capture fields, resolved across all entity
  // types in the org (backend sends these so the frontend doesn't need to
  // guess which entity type owns each capture field).
  const captureAttrsByCode = useMemo(() => {
    const map = new Map<string, { code_name: string; display_name: string; field_type: string; required: boolean; hint: string; description: string; configuration_data: Record<string, unknown> }>()
    for (const attr of evalResult?.capture_field_attributes ?? []) {
      map.set(attr.code_name, attr)
    }
    return map
  }, [evalResult?.capture_field_attributes])
  const resourceLookupProviders = useResourceLookupProviders()
  const queryClient = useQueryClient()
  const addressLookupApi = useMemo(
    () => createAddressLookupApi(queryClient),
    [queryClient],
  )
  const executeMutation = useExecuteEntityAction(
    resolvedUuid,
    resourceUuid,
    holdingUuid,
  )
  const certificateDownloadEnabled = useCertificateDownloadEnabled()
  const [confirmAction, setConfirmAction] = useState<EntityAction | null>(null)

  // ── Edit fields handling ──
  const [editAction, setEditAction] = useState<EntityAction | null>(null)

  // ── Exception handling ──
  // Only tenants with the exceptions capability enabled have these
  // endpoints; gating the queries keeps them from firing (and 404ing)
  // on every detail page for tenants without the feature.
  const exceptionsEnabled = useFeatureFlag(FLAGS.EXCEPTIONS)
  const tiersEnabled = useFeatureFlag(FLAGS.TIERS)
  const { data: bindings = [] } = useExceptionActionBindings(exceptionsEnabled)
  const { data: exceptionReasons = [] } = useExceptionReasons(exceptionsEnabled)
  const createExceptionGrant = useCreateExceptionGrant()
  const [exceptionAction, setExceptionAction] = useState<EntityAction | null>(null)
  const [exceptionGrantId, setExceptionGrantId] = useState<string | null>(null)
  // When an exception-gated action also captures fields, the flow is
  // exception modal → edit form → execute. The exception modal itself IS the
  // confirmation + reason surface (WPS-117): we never chain a second
  // confirmation/reason dialog after it. ``exceptionComment`` carries the
  // exception's justification through to the edit form's execute so the audit
  // log still records a reason.
  const [pendingEditAfterException, setPendingEditAfterException] =
    useState<EntityAction | null>(null)
  const [exceptionComment, setExceptionComment] = useState<string>('')

  // Build a lookup: action_key → binding (scoped to this entity type's resource_type)
  const bindingByActionKey = useMemo(() => {
    const map = new Map<string, ExceptionActionBinding>()
    for (const b of bindings) {
      if (b.is_active && b.resource_type === resolvedKey) {
        map.set(b.action_key, b)
      }
    }
    return map
  }, [bindings, resolvedKey])

  // Build reason options for exception modal
  const exceptionReasonOptions = useMemo<WorkflowIssueReasonOption[]>(() => {
    const options: WorkflowIssueReasonOption[] = exceptionReasons
      .filter((r) => r.is_active)
      .map((r) => ({ code: r.key, label: r.title, description: r.description }))
    return options
  }, [exceptionReasons])

  // Build FormFieldMeta for capture_fields on the current edit action.
  // Prefer cross-entity attribute definitions from the evaluate response
  // (which resolves across all entity types in the org), then fall back
  // to the local entity type's attributes, then to TEXT.
  const editFields = useMemo<FormFieldMeta[]>(() => {
    if (!editAction?.capture_fields?.length) return []
    const localAttrsByCode = new Map(
      (resolvedEntity?.attributes ?? []).map((a) => [a.code_name, a]),
    )
    return editAction.capture_fields.map((code) => {
      const crossAttr = captureAttrsByCode.get(code)
      if (crossAttr) {
        return defineField({
          code_name: crossAttr.code_name,
          display_name: crossAttr.display_name,
          field_type: crossAttr.field_type,
          required: crossAttr.required,
          hint: crossAttr.hint || crossAttr.description,
          options: crossAttr.configuration_data?.options as { label: string; value: string }[] | undefined,
          configuration_data: crossAttr.configuration_data,
        })
      }
      const attr = localAttrsByCode.get(code)
      if (attr) {
        return defineField({
          code_name: attr.code_name,
          display_name: attr.display_name,
          field_type: attr.field_type,
          required: attr.required,
          hint: attr.hint || attr.description,
          options: attr.configuration_data?.options,
          configuration_data: attr.configuration_data,
        })
      }
      return defineField({
        code_name: code,
        display_name: humaniseColumnName(code),
        field_type: 'TEXT',
      })
    })
  }, [editAction, resolvedEntity?.attributes, captureAttrsByCode])

  // Editing a customer's address warns, as it is typed, when another customer
  // of this tenant is already there. Advisory only - it never blocks saving.
  const editFieldNotices = useMemo(() => {
    if (resolvedKey !== 'consumer') return undefined
    const notices: Record<string, (value: unknown) => ReactNode> = {}
    for (const field of editFields) {
      if (field.field_type !== 'ADDRESS') continue
      notices[field.code_name] = (value) => (
        <AddressMatchNotice consumerUuid={resourceUuid} value={value} />
      )
    }
    return Object.keys(notices).length > 0 ? notices : undefined
  }, [resolvedKey, editFields, resourceUuid])

  // Current values for the edit form - pick only the capture fields.
  // Composite fields (ADDRESS, ENTITY_SEARCH) need special handling:
  // compose individual DB columns into the composite object the form
  // expects, using the component_columns mapping on the attribute.
  const editValues = useMemo<Record<string, unknown>>(() => {
    if (!editAction?.capture_fields?.length) return {}
    const localAttrsByCode = new Map(
      (resolvedEntity?.attributes ?? []).map((a) => [a.code_name, a]),
    )
    const values: Record<string, unknown> = {}
    for (const code of editAction.capture_fields) {
      const crossAttr = captureAttrsByCode.get(code)
      const localAttr = localAttrsByCode.get(code)
      const fieldType = crossAttr?.field_type ?? localAttr?.field_type
      const configData = crossAttr?.configuration_data ?? localAttr?.configuration_data
      const componentColumns = configData?.component_columns as
        | Record<string, string>
        | undefined
      const isComposite =
        (fieldType === 'ADDRESS' || fieldType === 'ENTITY_SEARCH') &&
        componentColumns
      if (isComposite) {
        values[code] = composeCompositeValue(componentColumns, resourceValues)
      } else {
        values[code] = resourceValues[code] ?? ''
      }
    }
    return values
  }, [editAction, resourceValues, resolvedEntity?.attributes, captureAttrsByCode])

  const [prevExternalTrigger, setPrevExternalTrigger] = useState<string | null>(null)
  if (externalTriggerActionKey && externalTriggerActionKey !== prevExternalTrigger) {
    setPrevExternalTrigger(externalTriggerActionKey)
    const action = actions?.find(
      (a) => a.key === externalTriggerActionKey && a.available !== false,
    )
    if (action) {
      if (collectsFields(action)) {
        setEditAction(action)
      } else if (action.action_kind === 'navigate') {
        onNavigate?.(action)
      } else {
        const needsConfirm =
          action.confirmation?.message || action.confirmation?.require_reason
        if (needsConfirm) setConfirmAction(action)
      }
    }
  } else if (!externalTriggerActionKey && prevExternalTrigger) {
    setPrevExternalTrigger(null)
  }
  // Hand the trigger back once it has been consumed above. Done after render:
  // resetting it is a state update in the PARENT, which React forbids during
  // this component's render ("Cannot update a component while rendering a
  // different component").
  useEffect(() => {
    if (externalTriggerActionKey) onExternalTriggerReset?.()
  }, [externalTriggerActionKey, onExternalTriggerReset])

  if (!actions || actions.length === 0) return null

  /** Expand composite values (ADDRESS, ENTITY_SEARCH) back into
   *  individual column names for the backend. */
  const expandCapturedValues = (
    captured: Record<string, unknown>,
  ): Record<string, unknown> => {
    const localAttrsByCode = new Map(
      (resolvedEntity?.attributes ?? []).map((a) => [a.code_name, a]),
    )
    const expanded: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(captured)) {
      const crossAttr = captureAttrsByCode.get(key)
      const localAttr = localAttrsByCode.get(key)
      const fieldType = crossAttr?.field_type ?? localAttr?.field_type
      const configData = crossAttr?.configuration_data ?? localAttr?.configuration_data
      const componentColumns = configData?.component_columns as
        | Record<string, string>
        | undefined
      const isComposite =
        (fieldType === 'ADDRESS' || fieldType === 'ENTITY_SEARCH') &&
        componentColumns
      if (isComposite) {
        // Decompose whether the value is set OR cleared. When the user
        // clears the picker the value is null/undefined; decomposing an
        // empty object emits every writable component column as "" so the
        // backend clears them (e.g. a removed contractor -> partnership = "").
        // Passing the raw null here instead would send the composite key
        // (e.g. "contractor") the backend can't map to a column, silently
        // dropping the clear - the WP1-121 bug.
        const objectValue =
          val && typeof val === 'object' ? (val as Record<string, string>) : {}
        Object.assign(
          expanded,
          decomposeCompositeValue(componentColumns, objectValue),
        )
      } else {
        expanded[key] = val
      }
    }
    return expanded
  }

  const handleExecute = async (
    action: EntityAction,
    values?: { captured_values: Record<string, unknown>; reason: string },
    grantId?: string | null,
  ) => {
    let result
    try {
      result = await executeMutation.mutateAsync({
        actionKey: action.key,
        captured_values: values?.captured_values
          ? expandCapturedValues(values.captured_values)
          : undefined,
        reason: values?.reason,
        exception_grant_id: grantId ?? exceptionGrantId,
      })
    } catch (err) {
      // Surface why an edit was rejected - e.g. re-validation blocked the
      // change (a reassigned/removed contractor left the warranty
      // ineligible). Re-throw so the dialog stays open and nothing is
      // treated as applied.
      toastApiError(err, `Could not complete "${action.label}"`)
      throw err
    }
    setExceptionGrantId(null)
    setExceptionComment('')

    const payload = result?.result ?? result
    if (
      certificateDownloadEnabled &&
      payload?.print_certificate &&
      payload?.organisation_uuid &&
      payload?.holding_uuid
    ) {
      try {
        const res = await api.post(
          `/organisations/${payload.organisation_uuid}/certificate-templates/preview/`,
          {
            template_slug: 'registration_certificate',
            holding_uuid: payload.holding_uuid,
          },
        )
        const html = res.data?.data?.html ?? res.data?.html
        if (!html) {
          toast.error('Certificate template not found')
        } else {
          const win = window.open('', '_blank')
          if (win) {
            win.document.write(html)
            win.document.close()
            setTimeout(() => win.print(), 600)
          }
        }
      } catch {
        toast.error('Failed to generate certificate')
      }
    }

    // A command action writes an EntityLog row, so the Activity card's
    // query has to be invalidated or the trail stays stale until a reload -
    // the operator cancels a warranty and the log looks empty. The
    // exception-grant mutation already invalidates this same key.
    queryClient.invalidateQueries({ queryKey: ENTITY_LOG_KEY })

    // Confirm the action landed. Until now a successful command action closed
    // its dialog and said nothing, so the only evidence anything had happened
    // was a value changing somewhere else on the page. For a tier change that
    // meant an agent altered a roof's coverage and price and received silence.
    //
    // A tier change names both ends of the move; every other action confirms
    // itself and points at where it was recorded.
    const oldTier = typeof payload?.old_tier === 'string' ? payload.old_tier : ''
    const newTier = typeof payload?.new_tier === 'string' ? payload.new_tier : ''
    // Named for what the agent did, not for the mechanism that recorded it.
    // "Exception applied" is our vocabulary; overriding is theirs.
    const handlerMessage =
      typeof payload?.message === 'string' ? payload.message : ''
    const overrideNote = (grantId ?? exceptionGrantId)
      ? 'Override recorded in the activity log.'
      : ''
    if (oldTier && newTier) {
      // The handler reports which way it moved. Read it, never infer it from
      // the action key: keys are a tenant's seed convention, so parsing them
      // announced a downgrade as an upgrade for any tenant that names theirs
      // something other than `downgrade_*`.
      const direction =
        payload?.direction === 'downgrade' ? 'downgraded' : 'upgraded'
      toast.success(`Warranty ${direction}`, {
        // Centred over the record and held longer than a routine success. This
        // is the moment the agent finds out their change landed and what it
        // changed to; at the default top-right for five seconds it was being
        // missed entirely, and pinned to the very top it sat over the page
        // title. Sonner has no true centre position, so the toaster is anchored
        // top-centre and the toast itself is pushed down (see the class).
        position: 'top-center',
        className: 'tier-change-toast',
        duration: TOAST_DURATION.confirmation,
        description: tiersEnabled ? (
          <span className="flex flex-col gap-1.5">
            <TierChange
              from={oldTier}
              to={newTier}
              fromFinish={payload?.old_tier_finish as string | undefined}
              toFinish={payload?.new_tier_finish as string | undefined}
              className="mt-1"
            />
            <span className="text-[11.5px] text-[var(--ink-3)]">
              {overrideNote || 'Recorded in the activity log.'}
            </span>
          </span>
        ) : (
          `${oldTier} to ${newTier}. ${overrideNote || 'Recorded in the activity log.'}`
        ),
      })
    } else if (handlerMessage) {
      // A handler may return a human message describing what it did. An
      // action whose whole effect is off-screen - sending a customer an
      // email, say - has no other way to confirm it happened (WPS-179).
      toast.success(handlerMessage, {
        description: overrideNote || undefined,
      })
    } else {
      toast.success(action.label, {
        description: overrideNote || 'Recorded in the activity log.',
      })
    }

    onActionComplete?.()
  }

  const handleSelect = (action: EntityAction) => {
    // Navigate is purely frontend - never needs exception or confirmation
    if (action.action_kind === 'navigate') {
      onNavigate?.(action)
      return
    }

    // ── A lock refuses outright ──
    // The tenant's rule says no exception may lift this (e.g. a warranty cannot
    // be upgraded once a claim is filed), so say why and stop - offering the
    // exception modal would invite an override the rule forbids.
    if (action.requires_override && action.override_allowed === false) {
      toast.error(action.override_reason || 'This action is not allowed here.')
      return
    }

    // ── Exception gate (checked first for ALL non-navigate actions) ──
    const binding = bindingByActionKey.get(action.key)
    // See needsExceptionCapture: a binding says an exception is possible,
    // eligibility says whether THIS record needs one. Both must agree.
    if (needsExceptionCapture(!!binding, action.requires_override)) {
      // The exception modal doubles as the confirmation + reason surface for
      // an exception-gated action (WPS-117 - no duplicate required comment).
      // ``edit_fields`` still needs its form afterwards to capture the real
      // field values; a command action executes straight after the grant.
      if (collectsFields(action)) {
        setPendingEditAfterException(action)
      }
      setExceptionAction(action)
      return
    }

    // ── No exception needed - proceed to the action's own flow ──
    if (collectsFields(action)) {
      if (onEditFields) {
        onEditFields(action)
      } else {
        setEditAction(action)
      }
      return
    }

    const needsConfirmation =
      action.confirmation?.message || action.confirmation?.require_reason
    if (needsConfirmation) {
      setConfirmAction(action)
      return
    }

    void handleExecute(action)
  }

  const handleExceptionConfirm = async (payload: WorkflowIssueResolvePayload) => {
    if (!exceptionAction) return
    const binding = bindingByActionKey.get(exceptionAction.key)
    const grant = await createExceptionGrant.mutateAsync({
      action_key: exceptionAction.key,
      reason_codes: payload.reason_codes ?? [],
      comment: payload.resolution_text ?? '',
      binding: binding?.uuid ?? null,
      // The grant covers this record only, so it cannot be replayed on another.
      entity_id: resourceUuid,
      context: {
        action_key: exceptionAction.key,
        action_label: exceptionAction.label,
      },
    })
    setExceptionGrantId(grant.uuid)
    const exceptionReason = payload.resolution_text ?? ''
    setExceptionComment(exceptionReason)

    if (pendingEditAfterException) {
      // The edit form comes next, so nothing else will confirm for a while.
      // On the immediate-execute path below, the action's own confirmation
      // carries the override instead: one action, one message.
      toast.success('Exception applied.')
      // Exception → edit form → execute. The edit form no longer asks for a
      // reason (the exception captured the justification); it flows through
      // as the reason at execute time.
      setExceptionAction(null)
      setEditAction(pendingEditAfterException)
      setPendingEditAfterException(null)
    } else {
      // The exception modal was the confirmation - execute now, recording the
      // exception's justification as the action reason so the audit log still
      // captures the original required comment (WPS-117).
      setExceptionAction(null)
      await handleExecute(
        exceptionAction,
        { captured_values: {}, reason: exceptionReason },
        grant.uuid,
      )
    }
  }

  const currentBinding = exceptionAction
    ? bindingByActionKey.get(exceptionAction.key)
    : null

  const excluded = excludeActionKeys ? new Set(excludeActionKeys) : null
  const items: CardActionMenuItem[] = [
    ...actions
      .filter((action) => !excluded?.has(action.key))
      .filter(
        (action) =>
          (!onlyHandlerKeys || onlyHandlerKeys.includes(action.handler_key)) &&
          !excludeHandlerKeys?.includes(action.handler_key),
      )
      .map((action) => {
        // A rule no exception may lift (a tier frozen by a filed claim, say):
        // shown, but locked, with the rule's own reason, so the agent sees it
        // before clicking rather than after.
        const locked =
          action.requires_override === true && action.override_allowed === false
        return {
        key: action.key,
        label: action.label,
        disabled: locked,
        note: locked ? action.override_reason || undefined : undefined,
        // A tier action shows the tier it moves to, in that tier's own finish,
        // so the rank is legible before the label is read. Falls back to the
        // configured icon for every other action, and whenever the capability
        // is off or the destination has no finish chosen.
        icon: locked ? (
          <Lock size={16} aria-label="Locked" />
        ) : (
          tierSwatchFor(action, tiersEnabled) ??
          (action.icon_name ? (
            <LucideIconByName name={action.icon_name} size={16} />
          ) : undefined)
        ),
        destructive: action.variant === 'destructive',
        section: menuSection(action),
        onSelect: () => handleSelect(action),
        }
      }),
    ...(extraItems ?? []),
  ]
  // Sectioned actions lead the menu, each section kept together in the order
  // it first appears; everything else keeps its own order below them.
  const sections = [...new Set(items.map((item) => item.section).filter(Boolean))]
  items.sort(
    (a, b) =>
      (a.section ? sections.indexOf(a.section) : sections.length) -
      (b.section ? sections.indexOf(b.section) : sections.length),
  )

  return (
    <>
      {hideTrigger ? null : (
        <CardActionMenu
          items={items}
          triggerClassName={triggerClassName}
          triggerLabel={triggerLabel}
          contentClassName={contentClassName}
          data-testid={triggerTestId}
        />
      )}

      <div
        className="contents"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {confirmAction && (
          <EntityActionExecuteDialog
            open={!!confirmAction}
            onOpenChange={(open) => {
              if (!open) {
                setConfirmAction(null)
                setExceptionGrantId(null)
              }
            }}
            action={confirmAction}
            isPending={executeMutation.isPending}
            onConfirm={async (values) => {
              await handleExecute(confirmAction, values)
              setConfirmAction(null)
            }}
          />
        )}

        {editAction && editFields.length > 0 && (
          <AddressLookupProvider api={addressLookupApi}>
            <ResourceLookupContextProvider providers={resourceLookupProviders}>
              <EditFormDialog
                open={!!editAction}
                onOpenChange={(open) => {
                  if (!open) setEditAction(null)
                }}
                title={editAction.label}
                fields={editFields}
                values={editValues}
                fieldNotices={editFieldNotices}
                requireReason={
                  (editAction.confirmation?.require_reason ?? false) &&
                  !bindingByActionKey.get(editAction.key)
                }
                isPending={executeMutation.isPending}
                onSave={async (formValues, reason) => {
                  await handleExecute(editAction, {
                    captured_values: formValues,
                    // Prefer the reason typed in the edit form; when the
                    // exception already captured the justification (a gated
                    // action), fall back to it so nothing is lost.
                    reason: reason || exceptionComment || '',
                  })
                  setEditAction(null)
                }}
              />
            </ResourceLookupContextProvider>
          </AddressLookupProvider>
        )}

        {exceptionAction && currentBinding && (
          <ExceptionModal
            uuid={null}
            open={!!exceptionAction}
            onOpenChange={(open) => {
              if (!open) {
                setExceptionAction(null)
                setPendingEditAfterException(null)
              }
            }}
            preview={{
              context: {
                subject: {
                  kind: 'Entity action',
                  title: exceptionAction.label,
                  subtitle: exceptionAction.key,
                },
                meta_sections: [],
                trigger: {
                  title: exceptionAction.label,
                  // The gated action's own confirmation copy becomes the
                  // exception modal's prompt - the modal IS the confirmation,
                  // so its message belongs here (WPS-117).
                  // Lead with what the agent would be stepping outside, when
                  // there is one. Clara's third law: every input has a why, and
                  // "this contractor is not entitled to Platinum" is the why
                  // that decides whether they should proceed at all. The
                  // configured copy follows as the what.
                  description: [
                    exceptionAction.override_reason,
                    exceptionAction.confirmation?.message ||
                      exceptionAction.description ||
                      'This action has been configured to require an exception before it can continue.',
                  ]
                    .filter(Boolean)
                    .join(' '),
                  breakdown: [],
                },
              },
              reasonOptions: exceptionReasonOptions,
              // The rule that refused names the exception that fits it; the
              // binding's per-action default is the fallback.
              defaultReasonCode:
                exceptionAction.suggested_exception_key ||
                currentBinding.suggested_exception_key ||
                undefined,
              showCommencementDate:
                exceptionAction.key === 'bypass_90_day_window',
              confirmLabel: 'Override exception',
              onConfirm: handleExceptionConfirm,
            }}
          />
        )}
      </div>
    </>
  )
}
